import { useMutation, useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import useCurrentUser from 'hooks/useCurrentUser';
import React, { useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import {
  ActivityIndicator,
  Button,
  Dialog,
  IconButton,
  Portal,
  Text,
  Divider,
  Caption,
} from 'react-native-paper';
import IntervalsProgress from 'shared/components/IntervalsProgress';
import ChartResult from 'shared/components/ChartResult';
import NotesContent from 'shared/components/NotesContent';
import NotesEditor from 'shared/components/NotesEditor';
import {
  WORKOUT_FRAGMENT,
  WORKOUT_RESULT_FRAGMENT,
} from 'shared/graphql/fragments';
import { colors, sizes } from 'shared/styles';
import {
  CompStatus,
  CurrentUser,
  DeviceStatus,
  HrSource,
  IntervalStatus,
  User,
  UserSettings,
  Workout,
  WorkoutResult,
  WorkoutResultStatus,
} from 'shared/types';
import { calculateDeviceTypes, formatFitScore } from 'shared/utils';
import styled from 'utils/styled';
import Link from './Link';
import RejectWorkoutResultModal, {
  RejectWorkoutResultConfirmPayload,
} from './RejectWorkoutResultModal';
import VerificationPhotosPlayer from 'shared/components/VerificationPhotosPlayer';
import useConfirmModal from 'shared/hooks/useConfirmModal';
import moment from 'moment';
import usePreferences from 'hooks/usePreferences';

interface Props {
  resultId?: string;
  onDismiss(): void;
}

interface ResultUser extends User {
  settings: UserSettings;
}

interface CurrentResult extends WorkoutResult {
  workout: Workout;
  user: CurrentUser;
}

interface QueryData {
  workoutResult: CurrentResult;
}

const GET_RESULT = gql`
  query GetResult($id: ID!) {
    workoutResult(id: $id) {
      ...ResultFragment
      verificationPhotos {
        url
        interval
        timestamp
      }
      workout {
        ...WorkoutFragment
      }
      user {
        id
        name
        settings {
          displayHeartRate
          maxHeartRate
          restingHeartRate
          weightClass
          gender
          benchmarkValues {
            distance
            name
            scoreNumber
            group
            field
            ergType
          }
        }
      }
    }
  }
  ${WORKOUT_FRAGMENT}
  ${WORKOUT_RESULT_FRAGMENT}
`;

const REJECT_WORKOUT_RESULT = gql`
  mutation RejectWorkoutResult($input: WorkoutResultManualRejectInput!) {
    rejected: workoutResultManualReject(input: $input) {
      ...ResultFragment
      workout {
        ...WorkoutFragment
      }
    }
  }
  ${WORKOUT_FRAGMENT}
  ${WORKOUT_RESULT_FRAGMENT}
`;

const Row = styled.view({
  flexDirection: 'row',
  borderBottomColor: colors.dividerColor,
  borderBottomWidth: sizes.pixel,
  padding: 10,
});

const ContentCell = styled.view({
  display: 'flex',
  margin: 10,
  padding: 1,
  flexDirection: 'row',
  flex: 0.8,
});

const HeaderCell = styled.view({
  borderBottomColor: '#ccc',
  marginBottom: '10px solid #ccc',
  display: 'flex',
  margin: 10,
  padding: 1,
  flexDirection: 'row',
  flex: 0.2,
});

const FitResult = ({ result }: { result: CurrentResult }) => {
  return (
    <View>
      <Row>
        <HeaderCell>
          <Text style={styles.boldText}>Workout</Text>
        </HeaderCell>
        <ContentCell>
          <Text>{result.workout.title}</Text>
        </ContentCell>
      </Row>
      <Row>
        <HeaderCell>
          <Text style={styles.boldText}>Description</Text>
        </HeaderCell>
        <ContentCell>
          <Text>{result.workout.description || '-'}</Text>
        </ContentCell>
      </Row>
      <Row>
        <HeaderCell>
          <Text style={styles.boldText}>Athlete</Text>
        </HeaderCell>
        <ContentCell>
          <Text>{result.user.name}</Text>
        </ContentCell>
      </Row>
      <Row>
        <HeaderCell>
          <Text style={styles.boldText}>Date</Text>
        </HeaderCell>
        <ContentCell>
          <Text>{new Date(result.insertedAt).toLocaleDateString()}</Text>
        </ContentCell>
      </Row>
      <Row>
        <HeaderCell>
          <Text style={styles.boldText}>Score</Text>
        </HeaderCell>
        <ContentCell>
          <Text>
            {formatFitScore(result.scoreValue, result.workout.scoreType)}
          </Text>
        </ContentCell>
      </Row>
      <Row>
        <HeaderCell>
          <Text style={styles.boldText}>Rx</Text>
        </HeaderCell>
        <ContentCell>
          <Text>{result.isRx ? 'Yes' : 'No'}</Text>
        </ContentCell>
      </Row>
      <Row>
        <HeaderCell>
          <Text style={styles.boldText}>Notes</Text>
        </HeaderCell>
        <ContentCell>
          <Text>{result.notes || '-'}</Text>
        </ContentCell>
      </Row>
      {result.externalVideoUrl && (
        <Row>
          <HeaderCell>
            <Text style={styles.boldText}>YouTube link</Text>
          </HeaderCell>
          <ContentCell>
            <Link to={result.externalVideoUrl}>{result.externalVideoUrl}</Link>
          </ContentCell>
        </Row>
      )}
    </View>
  );
};

const IntervalsResult = ({
  workoutResult,
  currentUser,
}: {
  workoutResult: CurrentResult;
  currentUser: CurrentUser;
}) => {
  const { getTableMetrics, setTableMetrics } = usePreferences();
  const { intervals, id, elapsedTime, elapsedDistance } = workoutResult;

  const workoutState = {
    intervals: intervals.map((i) => ({
      ...i,
      status:
        i.status === IntervalStatus.Inactive
          ? IntervalStatus.Inactive
          : IntervalStatus.Completed,
    })),
    ergType: workoutResult.ergType,
    hrSource: HrSource.MACHINE,
    workoutMode: workoutResult.workout.workoutMode!,
    userSettings: { ...workoutResult.user.settings, displayRollingStart: true },
    elapsedTime,
    elapsedDistance,
    deviceStatus: DeviceStatus.WorkoutEnded,
    workoutResultId: id,
    progress: 100,
    activeIndex: -1,
    deviceInfo: {},
    deviceTypes: calculateDeviceTypes(intervals),
    isPro: currentUser.appSubscription.active,
  };

  return (
    <IntervalsProgress
      sharing={false}
      currentUser={{
        ...workoutResult.user,
        settings: workoutState.userSettings,
      }}
      workoutState={workoutState}
      initialMetrics={getTableMetrics(workoutResult.ergType)}
      onMetricsChange={setTableMetrics}
      fontSizeVw={1.0}
      darkMode={false}
      hideLastRow
    />
  );
};
const ResultModal: React.FC<Props> = ({ resultId, onDismiss }) => {
  const { currentUser } = useCurrentUser();
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const confirmWith = useConfirmModal();

  const { data } = useQuery<QueryData>(GET_RESULT, {
    variables: { id: resultId },
    fetchPolicy: 'cache-and-network',
  });

  const [rejectWorkoutResult, { loading: rejecting }] = useMutation(
    REJECT_WORKOUT_RESULT
  );

  const openRejectDialog = () => setShowRejectDialog(true);

  const closeRejectDialog = () => setShowRejectDialog(false);

  const handleDismiss = () => {
    setShowRejectDialog(false);
    onDismiss();
  };

  const onConfirmReject = ({
    rejectedReason,
    sendInAppMessage,
  }: RejectWorkoutResultConfirmPayload) => {
    if (!resultId) return;
    rejectWorkoutResult({
      variables: {
        input: {
          id: resultId,
          rejectedReason,
          sendInAppMessage,
        },
      },
    })
      .then(() => {
        closeRejectDialog();
      })
      .catch(({ graphQLErrors }) => {
        const message =
          graphQLErrors && graphQLErrors.map((e: any) => e.message).join(', ');
        confirmWith({
          title: 'Error',
          message: message || 'Error rejecting result. Please, try again!',
          hideCancel: true,
        });
      });
  };

  const result = data && data.workoutResult;
  const allowEditNotes = result && result.user.id === currentUser.id;

  return (
    <Portal>
      <Dialog
        visible={!!resultId}
        onDismiss={handleDismiss}
        style={{
          height: '80vh',
          width: '60vw',
          alignSelf: 'center',
        }}
      >
        <View style={styles.modalHeader}>
          <Dialog.Title style={styles.modalTitle}>
            {result ? (
              <>
                {result.user.name}
                {'  '}
                <Caption>{moment(result.insertedAt).format('lll')}</Caption>
              </>
            ) : (
              ''
            )}
          </Dialog.Title>
          <IconButton
            icon="close"
            accessibilityLabel="Close"
            onPress={handleDismiss}
          />
        </View>
        {result ? (
          <Dialog.ScrollArea>
            <ScrollView>
              {!!result.workout.scoreType && <FitResult result={result} />}
              {result.intervals.length > 0 && (
                <>
                  <IntervalsResult
                    workoutResult={result}
                    currentUser={currentUser}
                  />

                  <ChartResult
                    currentUser={result.user}
                    workoutResult={result}
                    sharing={false}
                  />

                  {result.verificationPhotos &&
                    result.verificationPhotos.length > 0 && (
                      <View style={styles.verificationPhotosSection}>
                        <VerificationPhotosPlayer
                          photos={result.verificationPhotos}
                        />
                      </View>
                    )}

                  <View style={styles.notesSection}>
                    {isEditingNotes ? (
                      <View>
                        <View style={styles.notesSectionHeader}>
                          <Text style={styles.notesSectionTitle}>Notes</Text>
                        </View>
                        <Divider />
                        <View style={{ padding: sizes.spacing }}>
                          <NotesEditor
                            workoutResult={result}
                            workout={result.workout}
                            onSave={() => setIsEditingNotes(false)}
                            onCancel={() => setIsEditingNotes(false)}
                            actionsEnd
                          />
                        </View>
                      </View>
                    ) : (
                      <NotesContent
                        workout={result.workout}
                        workoutResult={result}
                        onEditNotes={
                          allowEditNotes
                            ? () => setIsEditingNotes(true)
                            : undefined
                        }
                      />
                    )}
                  </View>
                </>
              )}
            </ScrollView>
            {result.workout.track.compStatus && (
              <Dialog.Actions style={styles.actionsContainer}>
                {result.hasScore &&
                result.workout.track.compStatus !== CompStatus.Done ? (
                  <Button
                    mode="contained"
                    loading={rejecting}
                    disabled={rejecting}
                    icon="close-circle-outline"
                    onPress={openRejectDialog}
                    color={colors.destructiveColor}
                  >
                    REJECT RESULT
                  </Button>
                ) : (
                  <View style={{ alignItems: 'center' }}>
                    {result.status === WorkoutResultStatus.rejected &&
                      result.rejectedReason && (
                        <Caption>Reason: {result.rejectedReason}</Caption>
                      )}
                    {!!result.invalidReasonMessage && (
                      <Caption>{result.invalidReasonMessage}</Caption>
                    )}
                    <Button mode="outlined" disabled>
                      STATUS:{' '}
                      {result.status ||
                        (result.hasScore ? 'Ranked' : 'Not Ranked')}
                    </Button>
                  </View>
                )}
              </Dialog.Actions>
            )}
          </Dialog.ScrollArea>
        ) : (
          <Dialog.Content>
            <ActivityIndicator />
          </Dialog.Content>
        )}
      </Dialog>
      <RejectWorkoutResultModal
        visible={showRejectDialog}
        onDismiss={closeRejectDialog}
        loading={rejecting}
        onConfirm={onConfirmReject}
      />
    </Portal>
  );
};

const styles = StyleSheet.create({
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingRight: 4,
  },
  modalTitle: {
    flex: 1,
    marginBottom: 0,
    marginTop: -2,
  },
  actionsContainer: {
    paddingVertical: sizes.spacing,
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    alignItems: 'center',
  },
  boldText: {
    fontWeight: 'bold',
  },
  notesSection: {
    marginTop: sizes.spacing,
    marginHorizontal: sizes.spacing,
  },
  notesSectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: sizes.spacing / 2,
  },
  notesSectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  verificationPhotosSection: {
    marginHorizontal: sizes.spacing,
    marginTop: sizes.spacing,
  },
});

export default ResultModal;
